import React, { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { nl } from "date-fns/locale";
import {
   fetchShopPickupSlots,
   fetchShopVacationMode,
   placeOrder,
   fetchShopInfo,
} from "../../util/customer/httpCustomer";
import InputWithChecks from "../shared/InputWithChecks";
import PrimaryButtonDark from "../shared/PrimaryButtonDark";
import Container from "../shared/Container";
import LoadingIndicatorButton from "../shared/LoadingIndicatorButton";
import SuccessMessage from "../shared/SuccessMessage";
import {
   validateRequired,
   validateEmail,
   validateForm,
} from "../../util/shared/validation";

const breakDownSlotInHalfHourIncrements = (start, end) => {
   const [startHour, startMin] = start.split(":").map(Number);
   const [endHour, endMin] = end.split(":").map(Number);

   const startTotal = startHour * 60 + startMin;
   const endTotal = endHour * 60 + endMin;
   let duration = endTotal - startTotal; // in minuten

   // Helper functie om tijd in "HH:MM" terug te geven
   const minutesToTime = (totalMinutes) => {
      const h = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
      const m = String(totalMinutes % 60).padStart(2, "0");
      return `${h}:${m}`;
   };

   // Als duur <= 30 => één slot
   if (duration <= 30) {
      return [{ start, end }];
   }

   // Anders: terwijl duur > 60 minuten, haal er blokken van 30 minuten af.
   let chunks = [];
   let currentStart = startTotal;

   while (duration > 60) {
      const nextEnd = currentStart + 30;
      chunks.push({
         start: minutesToTime(currentStart),
         end: minutesToTime(nextEnd),
      });
      currentStart = nextEnd;
      duration = endTotal - currentStart;
   }

   // Nu is de resterende duur ≤ 60 minuten, dat wordt de laatste chunk
   if (duration > 0) {
      const finalStart = currentStart;
      const finalEnd = currentStart + duration;
      chunks.push({
         start: minutesToTime(finalStart),
         end: minutesToTime(finalEnd),
      });
   }

   return chunks.reverse();
};

const OrderForm = ({ cartItems }) => {
   const [selectedDate, setSelectedDate] = useState(null);
   const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
   const [successMessage, setSuccessMessage] = useState("");
   const [errors, setErrors] = useState({ date: false });

   const nameRef = useRef(null);
   const phoneRef = useRef(null);
   const emailRef = useRef(null);
   const timeSlotRef = useRef(null);

   const { data: pickupSlots, isLoading: isLoadingSlots } = useQuery({
      queryKey: ["pickupSlots"],
      queryFn: fetchShopPickupSlots,
   });

   const { data: vacationPeriods, isLoading: isLoadingVacations } = useQuery({
      queryKey: ["vacationPeriods"],
      queryFn: fetchShopVacationMode,
   });

   const {
      data: accountData,
      isLoading: isLoadingAccountData,
      isError: isErorrAccountData,
      error: errorAccountData,
   } = useQuery({
      queryKey: ["shopInfo"],
      queryFn: fetchShopInfo,
   });

   const { mutate, isPending, isError, error } = useMutation({
      mutationFn: placeOrder,
      onSuccess: (data) => {
         setSuccessMessage("Bestelling succesvol geplaatst!");
         setTimeout(() => {
            if (data?.paymentUrl) {
               const shopId = window.location.pathname.split('/')[1];
               localStorage.removeItem(`cartItems_${shopId}`);
               window.location.href = data.paymentUrl;
            }
         }, 1000);
         setTimeout(() => {
            setSuccessMessage("");
         }, 2000);
      },
   });

   if (isLoadingAccountData || isLoadingSlots || isLoadingVacations) {
      return <LoadingIndicatorButton />;
   }

   const days = [
      "zondag",
      "maandag",
      "dinsdag",
      "woensdag",
      "donderdag",
      "vrijdag",
      "zaterdag",
   ];

   console.log("Original pickup slots:", pickupSlots);

   for (let i = 0; i < days.length; i++) {
      if (pickupSlots[days[i]]?.length > 0) {
         let newSlots = [];

         for (const oldSlot of pickupSlots[days[i]]) {
            newSlots = [
               ...breakDownSlotInHalfHourIncrements(oldSlot.start, oldSlot.end),
               ...newSlots,
            ];
         }
         pickupSlots[days[i]] = [...newSlots];
      }
   }

   console.log("Processed pickup slots:", pickupSlots);
   console.log("Wednesday slots:", pickupSlots["woensdag"]);

   const handleDateChange = (date) => {
      setSelectedDate(date);
      setErrors((prev) => ({ ...prev, date: false }));
      if (date) {
         const currentDate = new Date();
         const isToday = date.toDateString() === currentDate.toDateString();
         
         // Get date components directly (month is already 0-based)
         const year = date.getFullYear();
         const month = date.getMonth();  // 0-based month (January is 0)
         const day = date.getDate();
         
         // Create a date at noon - no need to adjust month
         const consistentDate = new Date(year, month, day, 12, 0, 0);
         
         // Get day index reliably
         const dayIndex = consistentDate.getDay();
         const dayOfWeek = days[dayIndex];
         
         console.log("Selected date:", date);
         console.log("Selected date ISO:", date.toISOString());
         console.log("Date components:", { year, month, day });
         console.log("Consistent date:", consistentDate);
         console.log("Day index:", dayIndex);
         console.log("Day of week:", dayOfWeek);
         
         // Debug for Wednesday
         if (dayIndex === 3) {
            console.log("This is WEDNESDAY");
         }
         
         console.log("Available slots for this day:", pickupSlots?.[dayOfWeek]);

         // Filter de beschikbare tijdsloten op basis van de twee-uur regel
         const validSlots =
            pickupSlots?.[dayOfWeek]?.filter((slot) => {
               if (isToday) {
                  const slotStart = new Date();
                  slotStart.setHours(
                     slot.start.split(":")[0],
                     slot.start.split(":")[1],
                     0
                  );

              
                  return (
                     slotStart - currentDate >
                     accountData.shopMinimumOrderTimeBeforePickup *
                     60 *
                     60 *
                     1000
                  );
               }
               return true;
            }) || [];

         setAvailableTimeSlots(validSlots);

         // Als er geen geldige tijdsloten zijn, disable "vandaag"
         if (isToday && validSlots.length === 0) {
            setErrors((prev) => ({ ...prev, date: true }));
         }
      }
   };

   const getDisabledDates = () => {
      const currentDate = new Date();
      
      // Use the same approach as in handleDateChange
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();  // 0-based month (January is 0)
      const day = currentDate.getDate();
      
      // Create a date at noon to avoid timezone issues
      const consistentDate = new Date(year, month, day, 12, 0, 0);
      
      // Get day index reliably
      const todayIndex = consistentDate.getDay();
      const todayName = days[todayIndex];
      
      console.log("Today's date:", currentDate);
      console.log("Today components:", { year, month, day });
      console.log("Today consistent date:", consistentDate);
      console.log("Today's day index:", todayIndex);
      console.log("Today's day name:", todayName);

      const vacationRanges = vacationPeriods?.map((vacation) => ({
         from: new Date(vacation.vacationModeStart),
         to: new Date(vacation.vacationModeEnd),
      }));

      const dayOfWeek = [];
      let isTodayDisabled = !pickupSlots?.[todayName]?.some((slot) => {
         const slotStart = new Date();
         slotStart.setHours(
            slot.start.split(":")[0],
            slot.start.split(":")[1],
            0
         );
         return (
            slotStart - currentDate >
            accountData.shopMinimumOrderTimeBeforePickup * 60 * 60 * 1000
         );
      });

      let todayOrYesterday;

      if (isTodayDisabled) {
         todayOrYesterday = new Date();
      } else {
         todayOrYesterday = new Date();
         todayOrYesterday.setFullYear(1000);
      }

      for (let i = 0; i < days.length; i++) {
         if (pickupSlots[days[i]].length === 0) {
            dayOfWeek.push(i);
            console.log(`Disabling day: ${days[i]} (index ${i})`);
         }
      }

      return { vacationRanges, dayOfWeek, todayOrYesterday };
   };

   const handleFormSubmit = (e) => {
      e.preventDefault();

      const nameValid = nameRef.current?.validateInput();
      const phoneValid = phoneRef.current?.validateInput();
      const emailValid = emailRef.current?.validateInput();
      const dateValid = !!selectedDate;
      let timeSlotValid = true;

      if (selectedDate) {
         timeSlotValid = timeSlotRef.current?.validateInput();
      }

      setErrors({
         date: !dateValid,
      });

      if (nameValid && phoneValid && emailValid && dateValid && timeSlotValid) {
         // Get date components for debugging
         const year = selectedDate.getFullYear();
         const month = selectedDate.getMonth();  // 0-based month (January is 0)
         const day = selectedDate.getDate();
         
         // Create a consistent date
         const consistentDate = new Date(year, month, day, 12, 0, 0);
         
         // Get day index reliably
         const dayIndex = consistentDate.getDay();
         const dayName = days[dayIndex];
         
         console.log("Order date components:", { year, month, day });
         console.log("Order consistent date:", consistentDate);
         console.log("Order day index:", dayIndex);
         console.log("Order day name:", dayName);
         
         // Create a date string in YYYY-MM-DD format to avoid timezone issues
         // This ensures the date sent is exactly the date selected by the user
         const formattedLocalDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
         
         // Add day index marker to the formatted date
         const dateString = `Day${dayIndex}-${formattedLocalDate}`;
         
         console.log("Sending date with day marker:", dateString);
         
         // Calculate final prices for each cart item to send to the server
         const processedCartItems = cartItems.map(item => {
            // Calculate base price and options price
            const basePrice = parseFloat(item.price?.$numberDecimal || item.price) || 0;
            
            // Calculate options price
            let optionsPrice = 0;
            if (item.selectedOptions && item.productOptions) {
               Object.entries(item.selectedOptions).forEach(([index, value]) => {
                  const option = item.productOptions[index];
                  if (!option) return;
                  
                  if (option.type === "select") {
                     const selectedChoice = option.options[value];
                     const price = selectedChoice?.price?.$numberDecimal 
                        ? parseFloat(selectedChoice.price.$numberDecimal)
                        : parseFloat(selectedChoice?.price || 0);
                     optionsPrice += price;
                  } else if (option.type === "multiselect") {
                     value.forEach(choiceIndex => {
                        const choice = option.options[choiceIndex];
                        if (choice) {
                           const price = choice?.price?.$numberDecimal 
                              ? parseFloat(choice.price.$numberDecimal)
                              : parseFloat(choice?.price || 0);
                           optionsPrice += price;
                        }
                     });
                  }
               });
            }
            
            // Calculate total item price including options
            const totalPrice = basePrice + optionsPrice;
            
            // Apply discount if present
            const finalPrice = item.discount 
               ? totalPrice * (1 - item.discount / 100) 
               : totalPrice;
               
            return {
               _id: item._id,
               type: item.type,
               quantity: item.quantity,
               selectedOptions: item.selectedOptions,
               productOptions: item.productOptions,
               calculatedPrice: finalPrice // Send the pre-calculated price to the server
            };
         });
         
         const orderData = {
            name: nameRef.current.getValue(),
            phone: phoneRef.current.getValue(),
            email: emailRef.current.getValue(),
            date: dateString,
            timeSlot: {
               start: timeSlotRef.current?.getValue().split(" - ")[0],
               end: timeSlotRef.current?.getValue().split(" - ")[1],
            },
            cartItems: processedCartItems,
         };

         console.log("Order data being sent:", orderData);
         console.log("Day index explicitly sent:", dayIndex);

         mutate(orderData);
      }
   };

   const { vacationRanges, dayOfWeek, todayOrYesterday } = getDisabledDates();

   return (
      <Container>
         {successMessage ? (
            <SuccessMessage title="Succes!" description={successMessage} />
         ) : (
            <form onSubmit={handleFormSubmit} className="g-20">
               <h2>Plaats een Bestelling</h2>
               <div className="a-i-s">
                  {/* Naam */}
                  <InputWithChecks
                     ref={nameRef}
                     label="Naam"
                     inputType="text"
                     validate={validateRequired}
                     placeholder="Voer je naam in"
                  />

                  {/* Telefoonnummer */}
                  <InputWithChecks
                     ref={phoneRef}
                     label="Gsm-nummer"
                     inputType="text"
                     validate={validateRequired}
                     placeholder="Voer je telefoonnummer in"
                  />

                  <div className="f-v f-s-b">
                     <div className="g-20">
                        {/* E-mail */}
                        <InputWithChecks
                           ref={emailRef}
                           label="E-mail"
                           inputType="email"
                           validate={(value) =>
                              validateForm(value, [
                                 validateRequired,
                                 validateEmail,
                              ])
                           }
                           placeholder="Voer je e-mailadres in"
                        />

                        {/* Time Slots */}
                        {selectedDate && availableTimeSlots.length > 0 && (
                           <InputWithChecks
                              ref={timeSlotRef}
                              label="Selecteer een tijdslot"
                              inputType="select"
                              validate={(value) =>
                                 value ? "" : "Selecteer een geldig tijdslot"
                              }
                           >
                              <option value="">Selecteer een tijdslot</option>
                              {availableTimeSlots.map((slot, index) => (
                                 <option
                                    key={index}
                                    value={`${slot.start} - ${slot.end}`}
                                 >
                                    {slot.start} - {slot.end}
                                 </option>
                              ))}
                           </InputWithChecks>
                        )}
                     </div>
                                       {/* DayPicker */}
                  <div>
                     <label>Kies een ophaaldag</label>
                     <DayPicker
                        mode="single"
                        weekStartsOn={1}
                        selected={selectedDate}
                        onSelect={handleDateChange}
                        disabled={[
                           { dayOfWeek },
                           ...vacationRanges,
                           { before: new Date() },
                           todayOrYesterday,
                        ]}
                        locale={nl}
                     />
                     {errors.date && <p className="c-r">Kies een dag</p>}
                  </div>

                     <div className="g-10">
                        {isError && (
                           <p className="error">
                              {error.info?.message || "Er is iets misgegaan."}
                           </p>
                        )}
                        <div>
                           <PrimaryButtonDark type="submit" disabled={isPending}>
                              Bestelling Plaatsen
                              {isPending && <LoadingIndicatorButton />}
                           </PrimaryButtonDark>
                        </div>
                     </div>
                  </div>
               </div>
            </form>
         )}
      </Container>
   );
};

export default OrderForm;
